import { useState } from "react";

// Editor delle opzioni di prezzo tipizzate del wizard di prenotazione.
// Scrive su inputData.price ([{ value, type, note, minQty, maxQty, isSoldOut, people? }]) e mantiene
// isPremium allineato (l'effect in AddEditActivity lo azzera quando l'elenco si svuota).
// Le opzioni migrate dal vecchio modello conservano `people` (etichetta legacy per le app store).

const OPTION_TYPES = [
    { value: 'perPerson', label: 'A persona (CHF × partecipanti)' },
    { value: 'perHour', label: 'Orario (CHF × ore)' },
    { value: 'flat', label: 'Forfait (prezzo fisso)' }
];

const EMPTY_OPTION = { type: 'perPerson', value: '', note: '', minQty: '1', maxQty: '' };

// Etichetta riassuntiva dell'opzione nell'elenco admin
export const describePriceOption = (option) => {
    const unitLabel = option.type === 'perHour' ? 'ore' : 'partecipanti';
    if (option.type === 'perPerson') {
        return `${option.value} CHF / persona — da ${option.minQty ?? 1} a ${option.maxQty} ${unitLabel}`;
    }
    if (option.type === 'perHour') {
        return `${option.value} CHF / ora — da ${option.minQty ?? 1} a ${option.maxQty} ${unitLabel}`;
    }
    const maxPart = option.maxQty ? ` — massimo ${option.maxQty} partecipanti` : '';
    const legacyPeople = !option.maxQty && option.people ? ` — per ${option.people} persone` : '';
    return `${option.value} CHF forfait${maxPart}${legacyPeople}`;
};

const PriceOptionsEditor = ({ inputData, setInputData }) => {
    const [optionInput, setOptionInput] = useState(EMPTY_OPTION);

    const handleOptionInput = (event) => {
        const { id, value } = event.target;
        setOptionInput(prevState => ({
            ...prevState,
            [id.replace('option-', '')]: value
        }));
    };

    // Per i tipi con stepper utente (persona/ora) min e max sono obbligatori;
    // per il forfait il massimo partecipanti è facoltativo.
    const isStepperType = optionInput.type === 'perPerson' || optionInput.type === 'perHour';
    const minQty = Number(optionInput.minQty);
    const maxQty = optionInput.maxQty === '' ? null : Number(optionInput.maxQty);
    const canAdd = Number(optionInput.value) > 0 && (
        !isStepperType || (minQty >= 1 && maxQty !== null && maxQty >= minQty)
    );

    const addOption = () => {
        const newOption = {
            type: optionInput.type,
            value: Number(optionInput.value),
            note: optionInput.note.trim() !== '' ? optionInput.note.trim() : null,
            minQty: isStepperType ? minQty : 1,
            maxQty: maxQty
        };
        setInputData(prevState => ({
            ...prevState,
            price: [...prevState.price, newOption],
            isPremium: true
        }));
        setOptionInput(EMPTY_OPTION);
    };

    const removeOption = (index) => {
        setInputData(prevState => ({
            ...prevState,
            price: prevState.price.filter((element, i) => i !== index)
        }));
    };

    // Sold out della singola opzione (stato manuale, come per gli slot): l'opzione resta
    // visibile agli utenti ma non è più selezionabile e il server la rifiuta.
    const toggleSoldOut = (index, isSoldOut) => {
        setInputData(prevState => ({
            ...prevState,
            price: prevState.price.map((element, i) => i === index ? { ...element, isSoldOut } : element)
        }));
    };

    return (
        <div className="flex flex-col gap-2 w-full">
            <div className="flex flex-col gap-3 p-3 border-2 rounded">
                <div className="flex flex-col gap-2">
                    <label htmlFor="option-type">Tipo di prezzo</label>
                    <select id="option-type" className="w-full md:w-72" value={optionInput.type} onChange={handleOptionInput}>
                        {OPTION_TYPES.map(element => <option key={element.value} value={element.value}>{element.label}</option>)}
                    </select>
                </div>
                <div className="flex flex-col md:flex-row gap-2">
                    <div className="flex flex-col gap-2 flex-1">
                        <label htmlFor="option-value">
                            {optionInput.type === 'perPerson' && 'Prezzo a persona (CHF)'}
                            {optionInput.type === 'perHour' && 'Prezzo all\'ora (CHF)'}
                            {optionInput.type === 'flat' && 'Prezzo forfait (CHF)'}
                        </label>
                        <input type="number" min="0" id="option-value" value={optionInput.value} onChange={handleOptionInput} />
                    </div>
                    {
                        isStepperType &&
                        <>
                            <div className="flex flex-col gap-2 flex-1">
                                <label htmlFor="option-minQty">{optionInput.type === 'perPerson' ? 'Minimo partecipanti' : 'Minimo ore'}</label>
                                <input type="number" min="1" id="option-minQty" value={optionInput.minQty} onChange={handleOptionInput} />
                            </div>
                            <div className="flex flex-col gap-2 flex-1">
                                <label htmlFor="option-maxQty">{optionInput.type === 'perPerson' ? 'Massimo partecipanti' : 'Massimo ore'}</label>
                                <input type="number" min="1" id="option-maxQty" value={optionInput.maxQty} onChange={handleOptionInput} />
                            </div>
                        </>
                    }
                    {
                        optionInput.type === 'flat' &&
                        <div className="flex flex-col gap-2 flex-1">
                            <label htmlFor="option-maxQty">Massimo partecipanti (facoltativo)</label>
                            <input type="number" min="1" id="option-maxQty" value={optionInput.maxQty} onChange={handleOptionInput} />
                        </div>
                    }
                </div>
                <div className="flex flex-col gap-2">
                    <label htmlFor="option-note">Nota (facoltativa, es. "Noleggio attrezzatura incluso")</label>
                    <div className="flex gap-2">
                        <input type="text" id="option-note" className="flex-grow" value={optionInput.note} onChange={handleOptionInput} />
                        {canAdd && <button className="w-[72px] h-[72px] rounded-lg border-2 border-[#225424] text-[#225424] flex justify-center items-center text-4xl pb-1 shrink-0" onClick={addOption}>+</button>}
                        {!canAdd && <button className="w-[72px] h-[72px] rounded-lg border-2 border-neutral-300 text-neutral-300 flex justify-center items-center text-4xl pb-1 cursor-not-allowed shrink-0">+</button>}
                    </div>
                </div>
            </div>
            <div className="border-2 p-2 flex flex-col gap-2 min-h-[72px] rounded">
                {
                    inputData && inputData.price && inputData.price.length > 0 && inputData.price.map((element, index) => {
                        return (
                            <div key={index} className={`border-2 text-black py-2 px-3 rounded-lg flex flex-col gap-2 text-lg w-full ${element.isSoldOut ? 'border-red-500 bg-red-50' : 'border-[#225424]'}`}>
                                <div className="flex items-center justify-between gap-2">
                                    <div>
                                        {describePriceOption(element)}
                                        {element.isSoldOut && <span className="text-red-500 font-bold ml-2">SOLD OUT</span>}
                                        {element.note && <div className="text-sm italic">{element.note}</div>}
                                    </div>
                                    <div className="pt-2"><i className="fi fi-sr-circle-xmark text-xl cursor-pointer" onClick={() => removeOption(index)}></i></div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id={`option-soldOut-${index}`}
                                        checked={Boolean(element.isSoldOut)}
                                        onChange={(event) => toggleSoldOut(index, event.target.checked)}
                                    />
                                    <label htmlFor={`option-soldOut-${index}`} className="text-sm">Sold out</label>
                                </div>
                            </div>
                        )
                    })
                }
            </div>
        </div>
    )
}

export default PriceOptionsEditor;
